import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DonationService from '../../service/DonationService';
import styles from './DonationsPage.module.scss';

const DonationsPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [likedProducts, setLikedProducts] = useState(new Set());
  const [favoritedProducts, setFavoritedProducts] = useState(new Set());
  const [animatingLikes, setAnimatingLikes] = useState(new Set());
  const [animatingFavorites, setAnimatingFavorites] = useState(new Set());
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));

  useEffect(() => {
    // Проверяем авторизацию при монтировании
    const checkAuth = () => {
      setIsAuthenticated(!!localStorage.getItem('token'));
    };
    checkAuth();
    
    // Слушаем события изменения localStorage (при входе/выходе)
    window.addEventListener('storage', checkAuth);
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  useEffect(() => {
    // Загружаем категории независимо от авторизации
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchProducts(selectedCategory.id);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await DonationService.getDonationCategories();
      setCategories(response.data || []);
      setError(null);
    } catch (err) {
      setError('Ошибка загрузки категорий');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (categoryId) => {
    try {
      setLoading(true);
      const response = await DonationService.getDonationCategoryProducts(categoryId);
      setProducts(response.data || []);
      setError(null);
    } catch (err) {
      setError('Ошибка загрузки продуктов');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    if (!isAuthenticated) {
      return;
    }

    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item =>
      item.id === productId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2);
  };

  const toggleLike = (productId) => {
    if (!isAuthenticated) return;
    
    setAnimatingLikes(prev => new Set(prev).add(productId));
    
    setTimeout(() => {
      setAnimatingLikes(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }, 600);

    setLikedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleFavorite = (productId) => {
    if (!isAuthenticated) return;
    
    setAnimatingFavorites(prev => new Set(prev).add(productId));
    
    setTimeout(() => {
      setAnimatingFavorites(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }, 600);

    setFavoritedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      return;
    }
    // Оплата пока не доступна
  };

  const getCategoryImage = (category) => {
    if (category.image_url) return category.image_url;
    if (category.banner_url) return category.banner_url;
    if (category.icon_url) return category.icon_url;
    
    // Дефолтные изображения по типу
    const defaultImages = {
      'DirectTopUp': 'https://via.placeholder.com/300x200/4CAF50/ffffff?text=Direct+Top-Up',
      'VoucherCode': 'https://via.placeholder.com/300x200/2196F3/ffffff?text=Voucher+Code',
      'SteamTopUp': 'https://via.placeholder.com/300x200/171A21/ffffff?text=Steam'
    };
    
    return defaultImages[category.type] || 'https://via.placeholder.com/300x200/9E9E9E/ffffff?text=Donation';
  };

  if (loading && categories.length === 0) {
    return (
      <div className={styles.donationsPage}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p>Загрузка...</p>
        </div>
      </div>
    );
  }

  // Фильтрация категорий
  const filteredCategories = Array.isArray(categories) ? categories.filter(cat => {
    const matchesSearch = cat.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'All' || cat.type === filterType;
    return matchesSearch && matchesFilter;
  }) : [];

  return (
    <div className={styles.donationsPage}>
      <div className={styles.donationsHeader}>
        <h1>🎮 Донаты и пополнения</h1>
        <p className={styles.donationsSubtitle}>
          Быстрое пополнение игровых валют, покупка ваучеров и подписок
        </p>
      </div>

      {!isAuthenticated && (
        <div className={styles.authWarning}>
          <div className={styles.authWarningIcon}>🔒</div>
          <div className={styles.authWarningContent}>
            <h3>Требуется авторизация для покупки</h3>
            <p>Для добавления товаров в корзину и оформления заказа необходимо войти в аккаунт</p>
          </div>
          
        </div>
      )}

      {!selectedCategory ? (
        <>
          <div className={styles.donationsControls}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="🔍 Поиск категорий..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            <div className={styles.filterButtons}>
              <button
                className={`${styles.filterBtn} ${filterType === 'All' ? styles.active : ''}`}
                onClick={() => setFilterType('All')}
              >
                Все
              </button>
              <button
                className={`${styles.filterBtn} ${filterType === 'DirectTopUp' ? styles.active : ''}`}
                onClick={() => setFilterType('DirectTopUp')}
              >
                Прямое пополнение
              </button>
              <button
                className={`${styles.filterBtn} ${filterType === 'VoucherCode' ? styles.active : ''}`}
                onClick={() => setFilterType('VoucherCode')}
              >
                Коды ваучеров
              </button>
            </div>
          </div>

          <div className={styles.categoriesGrid}>
            {filteredCategories.length === 0 ? (
              <div className={styles.noResults}>
                <p>Категории не найдены</p>
              </div>
            ) : (
              filteredCategories.map(category => (
                <div
                  key={category.id}
                  className={styles.categoryCard}
                  onClick={() => setSelectedCategory(category)}
                >
                  <div className={styles.categoryImage}>
                    <img src={getCategoryImage(category)} alt={category.name} />
                    <div className={styles.categoryBadge}>
                      {category.type === 'DirectTopUp' && '⚡ Мгновенно'}
                      {category.type === 'VoucherCode' && '🎫 Код'}
                      {category.type === 'SteamTopUp' && '🎮 Steam'}
                    </div>
                  </div>
                  <div className={styles.categoryContent}>
                    <h3>{category.name}</h3>
                    {category.description && (
                      <p className={styles.categoryDescription}>{category.description}</p>
                    )}
                    <button className={styles.viewProductsBtn}>
                      Смотреть товары →
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <div className={styles.productsSection}>
          <div className={styles.productsHeader}>
            <button
              className={styles.backButton}
              onClick={() => {
                setSelectedCategory(null);
                setProducts([]);
              }}
            >
              ← Назад к категориям
            </button>
            <h2>{selectedCategory.name}</h2>
          </div>

          {selectedCategory.description && (
            <div className={styles.categoryInfo}>
              <p>{selectedCategory.description}</p>
            </div>
          )}

          {loading ? (
            <div className={styles.loadingSpinner}>
              <div className={styles.spinner}></div>
              <p>Загрузка продуктов...</p>
            </div>
          ) : (
            <div className={styles.productsGrid}>
              {products.length === 0 ? (
                <div className={styles.noResults}>
                  <p>Продукты не найдены</p>
                </div>
              ) : (
                products.map(product => (
                  <div
                    key={product.id}
                    className={`${styles.productCard} ${!product.is_available ? styles.unavailable : ''}`}
                  >
                    {product.image_url && (
                      <div className={styles.productImage}>
                        <img src={product.image_url} alt={product.name} />
                        {product.discount_percentage > 0 && (
                          <span className={styles.discountBadge}>-{product.discount_percentage}%</span>
                        )}
                      </div>
                    )}
                    
                    <div className={styles.productHeader}>
                      <h4>{product.name}</h4>
                      {product.is_popular && (
                        <span className={styles.popularBadge}>🔥 Популярно</span>
                      )}
                      {product.is_featured && (
                        <span className={styles.featuredBadge}>⭐ Рекомендуем</span>
                      )}
                    </div>

                    {product.short_description && (
                      <p className={styles.productShortDescription}>{product.short_description}</p>
                    )}
                    
                    {product.description && (
                      <p className={styles.productDescription}>{product.description}</p>
                    )}

                    {isAuthenticated && (
                      <div className={styles.productActions}>
                        <button
                          className={`${styles.likeBtn} ${likedProducts.has(product.id) ? styles.liked : ''} ${animatingLikes.has(product.id) ? styles.animating : ''}`}
                          onClick={() => toggleLike(product.id)}
                          title="Нравится"
                        >
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          className={`${styles.favoriteBtn} ${favoritedProducts.has(product.id) ? styles.favorited : ''} ${animatingFavorites.has(product.id) ? styles.animating : ''}`}
                          onClick={() => toggleFavorite(product.id)}
                          title="Добавить в избранное"
                        >
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="17 3 v4 M7 3 v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    )}

                    <div className={styles.productFooter}>
                      <div className={styles.priceSection}>
                        {product.original_price && product.original_price > product.price && (
                          <span className={styles.originalPrice}>{parseInt(product.original_price).toLocaleString('ru-RU')} сом</span>
                        )}
                        <span className={styles.currentPrice}>{parseInt(product.price).toLocaleString('ru-RU')} сом</span>
                      </div>

                      {selectedCategory.type === 'VoucherCode' && (
                        <div className={styles.stockInfo}>
                          {product.stock > 0 ? (
                            <span className={styles.inStock}>✓ В наличии: {product.stock}</span>
                          ) : (
                            <span className={styles.outOfStock}>✗ Нет в наличии</span>
                          )}
                        </div>
                      )}

                      <button
                        className={styles.addToCartBtn}
                        onClick={() => addToCart(product)}
                        disabled={!product.is_available || (selectedCategory.type === 'VoucherCode' && product.stock === 0)}
                      >
                        {!isAuthenticated
                          ? '🔒 Требуется авторизация'
                          : !product.is_available || (selectedCategory.type === 'VoucherCode' && product.stock === 0)
                          ? 'Недоступно'
                          : 'Добавить в корзину'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Плавающая кнопка корзины */}
      {cart.length > 0 && (
        <button
          className={styles.floatingCartBtn}
          onClick={() => setShowCart(!showCart)}
        >
          <svg className={styles.cartIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 3H5.5L5.6 3.5M7 13H17L21 5H6.5M7 13L5.6 3.5M7 13L5.5 15.5M17 13L18.5 15.5M7.5 18.5C7.5 19.0523 7.05228 19.5 6.5 19.5C5.94772 19.5 5.5 19.0523 5.5 18.5C5.5 17.9477 5.94772 17.5 6.5 17.5C7.05228 17.5 7.5 17.9477 7.5 18.5ZM19 18.5C19 19.0523 18.5523 19.5 18 19.5C17.4477 19.5 17 19.0523 17 18.5C17 17.9477 17.4477 17.5 18 17.5C18.5523 17.5 19 17.9477 19 18.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className={styles.cartCount}>{cart.length}</span>
          <span className={styles.cartTotal}>{parseInt(getTotalPrice()).toLocaleString('ru-RU')} сом</span>
        </button>
      )}

      {showCart && cart.length > 0 && (
        <div className={styles.cartOverlay} onClick={() => setShowCart(false)}>
          <div className={styles.cartModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.cartHeader}>
              <h3>🛒 Корзина</h3>
              <button className={styles.closeCart} onClick={() => setShowCart(false)}>
                ✕
              </button>
            </div>

            <div className={styles.cartItems}>
              {cart.map(item => (
                <div key={item.id} className={styles.cartItem}>
                  <div className={styles.cartItemInfo}>
                    <h4>{item.name}</h4>
                    <p className={styles.cartItemCategory}>{item.category_name}</p>
                  </div>
                  
                  <div className={styles.cartItemControls}>
                    <div className={styles.quantityControls}>
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                        −
                      </button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                        +
                      </button>
                    </div>
                    
                    <div className={styles.cartItemPrice}>
                      {parseInt(item.price * item.quantity).toLocaleString('ru-RU')} сом
                    </div>
                    
                    <button
                      className={styles.removeItemBtn}
                      onClick={() => removeFromCart(item.id)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.cartFooter}>
              <div className={styles.cartTotal}>
                <span>Итого:</span>
                <span className={styles.totalPrice}>{parseInt(getTotalPrice()).toLocaleString('ru-RU')} сом</span>
              </div>
              {isAuthenticated ? (
                <button className={styles.checkoutBtn} onClick={handleCheckout} disabled>
                  Оплата пока не доступна
                </button>
              ) : (
                <div className={styles.authRequired}>
                  <div className={styles.authRequiredIcon}>🔒</div>
                  <div className={styles.authRequiredText}>
                    <h4>Требуется авторизация</h4>
                    <p>Войдите в систему для оформления заказа</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className={styles.errorMessage}>
          <p>{error}</p>
          <button onClick={fetchCategories}>Попробовать снова</button>
        </div>
      )}
    </div>
  );
};

export default DonationsPage;
